import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { AddRpServiceConfigurationPropertyModal } from "../../../../modals/add-rp-service-configuration-property-modal/add-rp-service-configuration-property-modal";
import { RpServiceConfigurationPropertiesContainer } from "../../../../common/rp-service-configuration-properties-container/rp-service-configuration-properties-container";
import commonStyles from "../../verification-service-details-component.less";

type FederatedIdpPropertiesTabContainerProps = {
    isDisabled: boolean;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const FederatedIdpPropertiesTabContainer = (props: FederatedIdpPropertiesTabContainerProps) => {
    const { isDisabled, rpServiceConfigurationState, setRpServiceConfigurationState } = props;
    const modalContext = useModalContext();

    const federatedIdpPropertyIds = useMemo(() => {
        return rpServiceConfigurationState.properties.filter(property => property.category === "idp").map(property => property.propertyTypeId);
    }, []);

    const permissionBasedIsDisabled = isDisabled || !rpServiceConfigurationState.permissions.canModifyRPServiceProperties;
    const unselectedFederatedIdpProperties = rpServiceConfigurationState.properties.filter(property => federatedIdpPropertyIds.includes(property.propertyTypeId) && !property.addedToService);
    const selectedFederatedIdpProperties = rpServiceConfigurationState.properties.filter(property => federatedIdpPropertyIds.includes(property.propertyTypeId) && property.addedToService);

    const addRpServiceConfigurationProperty = (rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty) => {
        const newProperties = rpServiceConfigurationState.properties.map(property => property.propertyTypeId === rpServiceConfigurationProperty.propertyTypeId
            ? rpServiceConfigurationProperty
            : property
        );
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const onClickAddFederatedIdpProperty = () => {
        handleRenderAddPropertyModal(unselectedFederatedIdpProperties);
    };

    const handleRenderAddPropertyModal = (properties: Array<FormattedRpServiceConfigurationProperty>) => {
        const renderAddPropertyModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationPropertyModal
                    rpServiceConfigurationProperties={properties}
                    addRpServiceConfigurationProperty={addRpServiceConfigurationProperty}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddPropertyModal,
            modalType: ModalTypes.AddProperty
        });
    };

    const federatedIdpPropertiesHeaderButton = (
        (!permissionBasedIsDisabled) ? (
            <Button disabled={unselectedFederatedIdpProperties.length === 0} onClick={onClickAddFederatedIdpProperty}>
                {resources.pages.verificationServiceDetails.tabs.federatedIdp.subTabs.properties.buttons.addProperty.label}
            </Button>
        ) : null
    );

    return (
        <div>
            <ContentPageHeader
                title={resources.pages.verificationServiceDetails.tabs.federatedIdp.subTabs.properties.title}
                headerButton={federatedIdpPropertiesHeaderButton}
            />
            <ContentPageBody isMaxWidth={true}>
                <span className={commonStyles.description}>
                    {resources.pages.verificationServiceDetails.tabs.federatedIdp.subTabs.properties.description}
                </span>
                <div>
                    {!rpServiceConfigurationState.permissions.canModifyRPServiceProperties && (
                        <Alert variant={"warning"}>
                            {resources.pages.verificationServiceDetails.tabs.common.alerts.notAuthorizedToModifyProperties}
                        </Alert>
                    )}
                    {unselectedFederatedIdpProperties.length === 0 && (
                        <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.federatedIdp.subTabs.properties.allPropertiesMessage}</Alert>
                    )}
                    {selectedFederatedIdpProperties.length === 0 && (
                        <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.federatedIdp.subTabs.properties.noPropertiesMessage(permissionBasedIsDisabled)}</Alert>
                    )}
                    {selectedFederatedIdpProperties.length > 0 && (
                        <RpServiceConfigurationPropertiesContainer
                            isDisabled={permissionBasedIsDisabled}
                            selectedRpServiceConfigurationProperties={selectedFederatedIdpProperties}
                            rpServiceConfigurationState={rpServiceConfigurationState}
                            setRpServiceConfigurationState={setRpServiceConfigurationState}
                        />
                    )}
                </div>

            </ContentPageBody>
        </div >
    );
};